import DISCOUNTS from 'constants/discounts';
import { DealTypes, DiscountTypes } from 'api';
import { BrandsEnum } from 'api/types/Brands';
import { CountriesEnum } from 'api/types/Countries';
import { AutocompleteSearchItem } from 'api/types/common';

export const initialModel: DiscountTypes.ItemCreateParams = {
  brand: BrandsEnum.avaAndMay,
  country_team: CountriesEnum.DE,
  strategy: DealTypes.StrategiesEnum.influencers,
  channel: DealTypes.ChannelInfluencerEnum.InstagramStories,
  discount_type: DiscountTypes.DiscountTypeEnum.Percentage,
  customer_eligibility: DiscountTypes.CustomerEligibilityEnum.Everyone,
  discount_amount_type: DiscountTypes.DiscountAmountTypeEnum.ProductDiscount,
  discount_details: DiscountTypes.DetailsEnum.PercentageDiscount,
  minimum_requirements: DiscountTypes.MinimumRequirementsEnum.None,
  coupon_code: '',
  coupon_description: '',
  shop_id: '',
  end_date: undefined,
  start_date: '',
  value: '' as any, // should be "0" but removed to keep init value empty field
  minimum_value: '' as any, // should be "0" but removed to keep init value empty field
  usage_limit: null,
  applies_once_per_customer: false,
  applies_once_per_order: false,
  products: [],
  collections: [],
  customer_segments: [],
  customers_individual: [],
  free_gift_products: [],
  free_gift_product_categories: [],
  applies_to: DiscountTypes.AppliesToEnum.SpecificCollections,
};

export const dataToInitialModel = (
  data: DiscountTypes.ItemData,
): DiscountTypes.ItemCreateParams => ({
  shop_id: data.shop_id.$oid,
  brand: data.brand,
  channel: data.channel,
  country_team: data.country_team,
  start_date: data.start_date,
  strategy: data.strategy,
  end_date: data.end_date,
  products: data.products || [],
  coupon_code: data.coupon_code,
  coupon_description: data.coupon_description,
  discount_type: data.discount_type,
  applies_to: data.applies_to,
  customer_eligibility: data.customer_eligibility,
  discount_amount_type: data.discount_amount_type,
  discount_details: data.discount_details,
  minimum_requirements: data.minimum_requirements,
  value: data.value,
  minimum_value: data.minimum_value,
  usage_limit: data.usage_limit,
  applies_once_per_customer: data.applies_once_per_customer,
  applies_once_per_order: data.applies_once_per_order,
  collections: data.collections || [],
  customer_segments: data.customer_segments || [],
  customers_individual: data.customers_individual || [],
  free_gift_products: data.free_gift_products || [],
  free_gift_product_categories: data.free_gift_product_categories || [],
  app_description: data.app_description,
  app_function_id: data.app_function_id,
  metafields_attributes: data.metafields?.map((m) => {
    const { _id, ...newMeta } = m;

    if ('prefix' in newMeta.value && newMeta.value.prefix) {
      newMeta.value.prefix = newMeta.value.prefix.toLowerCase();
    }

    return { ...newMeta, id: _id.$oid };
  }),
});

export const dataToCloneModel = (
  data: DiscountTypes.ItemData,
): DiscountTypes.ItemCreateParams => {
  const model = dataToInitialModel(data);

  return {
    ...model,
    coupon_code: '',
  };
};

export const dataInitialSelectsOptions = (data: DiscountTypes.ItemData) => {
  const products: AutocompleteSearchItem[] = data.products.map((p) => ({
    _id: { $oid: p.id },
    name: p.title,
    image: p.image,
  }));
  const collections: AutocompleteSearchItem[] = data.collections.map((p) => ({
    _id: { $oid: p.id },
    name: p.title,
    image: p.image,
  }));
  const customerSegments: AutocompleteSearchItem[] = data.customer_segments.map(
    (p) => ({
      _id: { $oid: p.id },
      name: p.name,
    }),
  );
  const customersIndividual: AutocompleteSearchItem[] =
    data.customers_individual.map((p) => ({
      _id: { $oid: p.id },
      name: p.name,
    }));
  const metafieldsCollections: AutocompleteSearchItem[] =
    data.metafields &&
    data.metafields[0]?.value &&
    'selectCollection' in data.metafields[0].value &&
    data.metafields[0]?.value?.selectCollection
      ? data.metafields[0].value.selectCollection.map((p) => ({
          _id: { $oid: p.id },
          name: p.title,
          image: p.image,
        }))
      : [];
  const metafieldsProducts: AutocompleteSearchItem[] =
    data.metafields &&
    data.metafields[0]?.value &&
    'products' in data.metafields[0].value &&
    data.metafields[0]?.value?.products
      ? data.metafields[0].value.products.map((p) => ({
          _id: { $oid: p.id },
          name: p.title,
          image: p.image,
        }))
      : [];

  return {
    products,
    collections,
    customerSegments,
    customersIndividual,
    metafieldsCollections,
    metafieldsProducts,
  };
};

export const checkExternalDiscount = (code: string) => {
  const codeUpper = code.toUpperCase();

  const matchedPrefixes = DISCOUNTS.PREFIXES.filter((prefix) =>
    codeUpper.startsWith(prefix),
  );
  const matchedSuffixes = DISCOUNTS.SUFFIXES.filter((suffix) =>
    codeUpper.endsWith(suffix),
  );

  return {
    hasMatch: matchedPrefixes.length > 0 || matchedSuffixes.length > 0,
    hasBoth: matchedPrefixes.length > 0 && matchedSuffixes.length > 0,
    hasPrefix: matchedPrefixes.length > 0,
    hasSuffix: matchedSuffixes.length > 0,
    matchedPrefixes,
    matchedSuffixes,
  };
};

export const getCouponHelperText = (couponCode: string) => {
  const discountInfo = checkExternalDiscount(couponCode);

  if (!discountInfo.hasMatch) {
    return '';
  }

  const prefixes = discountInfo.matchedPrefixes.join(', ');
  const suffixes = discountInfo.matchedSuffixes.join(', ');

  const prefixesText =
    prefixes.length > 1 ? `prefixes "${prefixes}"` : `prefix "${prefixes[0]}"`;

  const suffixesText =
    suffixes.length > 1 ? `suffixes "${suffixes}"` : `suffix "${suffixes[0]}"`;

  let message = 'The discount you entered is using ';

  if (discountInfo.hasBoth) {
    message += `both ${prefixesText} and ${suffixesText}`;
  } else {
    if (discountInfo.hasPrefix) {
      message += prefixesText;
    }

    if (discountInfo.hasSuffix) {
      message += suffixesText;
    }
  }

  return `${message}. Please make sure that discount code value is 0.`;
};

export const discountLabels: Record<
  DiscountTypes.DiscountTypeItem['namespace'],
  string
> = {
  apply_quantity_based_free_item_discount: 'Discount + Buy X Get Y',

  'product-discount-prefix': 'Prefix Based Discount',

  'free-gift-and-cart-value-combo': 'Free gift and price tiers discount',
  discount_by_product_and_quantity: 'Discount + Product Quantity Tiers (ADV)',

  'Product Qunatity Based Discount': 'Product Quantity Tiers',
};

// export const dTypes = Object.entries(discountLabels).map(([key, value]) => ({
//   app_function_id: key,
//   app_description: key,
//   label: value,
// }));

export const getInitialMetaFieldValue = (
  type: DiscountTypes.DiscountTypeItem['namespace'],
) => {
  switch (type) {
    case 'free-gift-and-cart-value-combo': {
      const v: DiscountTypes.FreeItemDiscountParams = {
        cart_value_tiers: [],
        mixmatch: false,
        basis: DiscountTypes.BasisEnum.price,
        selectedCollectionIds: [],
        selectCollection: [],
      };
      return v;
    }
    case 'product-discount-prefix': {
      const v: DiscountTypes.PrefixDiscountParams = {
        prefix: '',
        basis: DiscountTypes.BasisEnum.price,
        message: 'discounted',
        mixmatch: false,
        title: '',
        minimumSubtotal: 0,
        percentOffOnShipping: 0,
      };
      return v;
    }
    case 'apply_quantity_based_free_item_discount': {
      const v: DiscountTypes.BuyOneGetOneDiscountParams = {
        percent_off_on_collection: 0,
        buy_x: 0,
        get_y: 0,
        incremental_buy_x_get_y: false,
        mixmatch: false,
      };
      return v;
    }
    case 'Product Qunatity Based Discount': {
      const v: DiscountTypes.ProductQuantityBasedDiscountParams = {
        basis: DiscountTypes.BasisEnum.price,
        message: 'discounted',
        mix_and_match: false,
        selectCollection: [],
      };
      return v;
    }
    case 'discount_by_product_and_quantity': {
      const v: DiscountTypes.ProductAndQuantityDiscountParams = {
        percent_off_on_collection: 0,
        target_variant_ids: [],
        products: [],
        qty_tiers: [],
        no_discount_applicable_tag: '',
        basis: DiscountTypes.BasisEnum.price,
        mixmatch: false,
      };
      return v;
    }
    default:
      return null;
  }
};
